import { Link, useNavigate } from 'react-router';
import { BellIcon, ClockIcon } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNotifications } from './notifications-store';
import { KIND_META } from '@/lib/notifications';
import { useT } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { cn } from '@/lib/utils';

/**
 * Topbar notification popup — the kit's `layout/header/notifications`
 * component: bell with an unread dot, sticky "Notifications / View all"
 * header, and a 350px scrolling list of avatar + title + description + time
 * rows, with inline Accept / Decline on the items that ask for a decision.
 *
 * Differences from the kit, both deliberate:
 *   - the unread dot renders only when something is actually unread, and
 *     clicking a row marks it read, so the dot means what it says;
 *   - identity is initials, and kind icons use theme tokens — no remote avatar
 *     URLs, nothing hard-coded to a palette (see lib/notifications.ts).
 *
 * THE FEED IS EMPTY until a read API exists. It used to hold six invented rows
 * behind a "Sample" badge; see `lib/notifications.ts` for why that was removed
 * rather than relabelled.
 */
export function NotificationsMenu() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { items, unreadCount, markRead, decide } = useNotifications();
  const t = useT();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="relative"
          aria-label={
            unreadCount
              ? t('chrome.notificationsUnread', { count: unreadCount })
              : t('chrome.notifications')
          }
        >
          <BellIcon className="size-4" />
          {unreadCount > 0 && (
            <span className="bg-destructive absolute end-1.5 top-1.5 block size-1.5 shrink-0 rounded-full" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align={isMobile ? 'center' : 'end'} className="w-80 p-0">
        <DropdownMenuLabel className="bg-background dark:bg-muted sticky top-0 z-10 p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="font-medium">{t('chrome.notifications')}</span>
            <Button variant="link" className="h-auto p-0 text-xs" asChild>
              <Link to="/notifications">{t('chrome.viewAll')}</Link>
            </Button>
          </div>
        </DropdownMenuLabel>

        {/* An empty feed says why it is empty. A blank 350px box reads as a
            component that failed to load, which is how the invented feed that
            used to fill it was read in the other direction. */}
        {items.length === 0 ? (
          <p className="text-muted-foreground px-4 py-8 text-center text-xs text-balance">
            {t('chrome.notificationsEmpty')}
          </p>
        ) : (
        <ScrollArea className="h-[350px]">
          {items.map((item) => {
            const { icon: Icon, tone } = KIND_META[item.kind];
            return (
              <DropdownMenuItem
                key={item.id}
                className="group flex cursor-pointer items-start gap-3 rounded-none border-b px-4 py-3"
                // Opening the item is what marks it read; the page is where the
                // detail lives, so the row is a link to it in effect.
                onSelect={() => {
                  markRead(item.id);
                  navigate('/notifications');
                }}
              >
                <div className="flex-none">
                  {item.actor ? (
                    <Avatar className="size-8">
                      <AvatarFallback className="text-xs">{item.actor.initials}</AvatarFallback>
                    </Avatar>
                  ) : (
                    <span
                      className={cn(
                        'flex size-8 items-center justify-center rounded-full',
                        tone === 'danger'
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-primary/10 text-primary',
                      )}
                    >
                      <Icon className="size-4" />
                    </span>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-1">
                  <div className="truncate text-sm font-medium">{item.title}</div>
                  <div className="text-muted-foreground line-clamp-1 text-xs">
                    {item.description}
                  </div>

                  {item.requiresDecision && (
                    <div className="flex items-center gap-2 py-1">
                      <Button
                        size="xs"
                        variant="outline"
                        // The row's own onSelect would close the menu and
                        // navigate before the decision registered.
                        onClick={(e) => {
                          e.stopPropagation();
                          decide(item.id, 'accepted');
                          notify.success(t('chrome.accepted.title'), item.title);
                        }}
                      >
                        {t('chrome.accept')}
                      </Button>
                      <Button
                        size="xs"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          decide(item.id, 'declined');
                          notify.info(t('chrome.declined.title'), item.title);
                        }}
                      >
                        {t('chrome.decline')}
                      </Button>
                    </div>
                  )}

                  <div className="text-muted-foreground flex items-center gap-1 text-xs">
                    <ClockIcon className="size-3" />
                    {item.time}
                  </div>
                </div>

                {item.unread && (
                  <span className="bg-destructive/80 mt-1 block size-2 flex-none rounded-full" />
                )}
              </DropdownMenuItem>
            );
          })}
        </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
